import { friendlyCampaignError } from '../utils/campaignErrors';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { campaignErrorMessage, decideMetaEdit, decideGoogleBudgetEdit, getActivity, getConnection, getPerformance, undoApply } from '../api/adpilotApi';
import MetaEditReview from './MetaEditReview';
import { demoActivity, demoPerformance } from '../demoData';
import { applyToolFor, channelFor } from '../channels';
import ChannelTabs, { readActiveChannel } from './ChannelTabs';

const dollars = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const centsToDollars = (cents) => (
  cents === null || cents === undefined ? '—' : dollars.format(Number(cents) / 100)
);
const displayValue = (value, field = '', currency = 'USD') => (/_micros$/.test(field) && Number.isFinite(Number(value))
  ? new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(value) / 1_000_000)
  : /^(daily_budget|spend_limit|bid_amount)$/.test(field) && Number.isFinite(Number(value))
  ? centsToDollars(value)
  : Array.isArray(value)
  ? value.map((item) => displayValue(item, field, currency)).join(', ')
  : value && typeof value === 'object'
    ? Object.entries(value).filter(([, item]) => item != null && item !== '')
      .map(([key, item]) => `${key.replaceAll('_', ' ')}: ${displayValue(item, key, currency)}`).join('; ')
    : String(value));
const changeValues = (apply) => {
  const requested = apply.requested_state || {};
  const suggested = requested.suggested_values && typeof requested.suggested_values === 'object'
    ? requested.suggested_values
    : requested;
  const current = apply.before_state?.current_value ?? requested.current_value ?? suggested.current_value;
  const recommended = apply.after_state?.current_value ?? requested.recommended_value ?? suggested.recommended_value
    ?? (requested.suggested_values ? suggested : null);
  const currency = suggested.account_currency || requested.account_currency || 'USD';
  const prefix = apply.undo_of
    ? (apply.status === 'failed' ? 'Undo attempted: ' : 'Undo: ')
    : apply.status === 'reverted' ? 'Reverted: '
      : apply.status === 'failed' ? 'Attempted: ' : '';
  if (current != null && recommended != null) {
    return apply.status === 'reverted'
      ? `${prefix}${displayValue(recommended, '', currency)} → ${displayValue(current, '', currency)}`
      : `${prefix}${displayValue(current, '', currency)} → ${displayValue(recommended, '', currency)}`;
  }
  if (apply.status === 'failed' && recommended != null) return `${prefix}${displayValue(recommended, '', currency)}`;
  const beforeBudget = apply.before_state?.current_budget_cents ?? requested.current_budget_cents;
  const afterBudget = requested.new_budget_cents ?? apply.after_state?.new_budget_cents;
  if (beforeBudget != null && afterBudget != null) return `${prefix}${centsToDollars(beforeBudget)} → ${centsToDollars(afterBudget)}`;
  return apply.status === 'failed' && afterBudget != null ? `${prefix}${centsToDollars(afterBudget)}` : null;
};
const applyStatus = (apply) => (apply.undo_of
  ? ({ pending: 'undoing', applied: 'undone', failed: 'undo failed' }[apply.status] || apply.status)
  : apply.status);
const noDeliveryAdvice = 'No delivery in this period. Try a longer analysis window or choose an active campaign.';
const shortDate = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

function runWindow(run) {
  if (!run.reporting_start_date || !run.reporting_end_date) return null;
  const start = new Date(`${String(run.reporting_start_date).slice(0, 10)}T00:00:00Z`);
  const end = new Date(`${String(run.reporting_end_date).slice(0, 10)}T00:00:00Z`);
  const days = Math.round((end - start) / (24 * 60 * 60 * 1000)) + 1;
  return `Window ${shortDate.format(start)} – ${shortDate.format(end)} · ${days} days`;
}

function ActivityPage({ user }) {
  const [activeChannel, setActiveChannel] = useState(readActiveChannel);
  const channel = channelFor(activeChannel);
  const [activity, setActivity] = useState(user.preview ? demoActivity : { applies: [], runs: [] });
  const [hasConnection, setHasConnection] = useState(user.preview ? true : null);
  const [performance, setPerformance] = useState(user.preview ? demoPerformance : { campaigns: [] });
  const [error, setError] = useState(null);
  const [undoing, setUndoing] = useState(null);
  const [checkingEdit, setCheckingEdit] = useState(null);
  const [canReviewEdits, setCanReviewEdits] = useState(false);
  const loadGeneration = useRef(0);
  const inFlight = useRef(false);
  const performanceLoadedAt = useRef(0);
  const userId = user.id;

  const load = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    const generation = loadGeneration.current;
    try {
      let current;
      try {
        current = (await getConnection({ userId, provider: activeChannel })).connection;
      } catch (connError) {
        if (generation !== loadGeneration.current) return;
        if (connError?.response?.status === 404) {
          setHasConnection(false);
          setActivity({ applies: [], runs: [] });
          return;
        }
        throw connError;
      }
      if (generation !== loadGeneration.current) return;
      setHasConnection(Boolean(current?.connection_id));
      setCanReviewEdits(Boolean(current?.connection_id) && (!current.access_role || current.access_role === 'owner'));
      if (current?.connection_id) {
        const nextActivity = await getActivity({ userId, connectionId: current.connection_id });
        if (generation !== loadGeneration.current) return;
        setActivity(nextActivity);
        if (Date.now() - performanceLoadedAt.current > 60 * 1000) {
          try {
            const nextPerformance = await getPerformance({ userId, connectionId: current.connection_id });
            if (generation !== loadGeneration.current) return;
            setPerformance(nextPerformance);
            performanceLoadedAt.current = Date.now();
          } catch (performanceError) {
            if (generation !== loadGeneration.current) return;
            setError(campaignErrorMessage(performanceError, `Failed to load ${channel.label} performance`));
          }
        }
      }
    } catch (loadError) {
      if (generation !== loadGeneration.current) return;
      setError(campaignErrorMessage(loadError, 'Failed to load activity'));
    } finally {
      if (generation === loadGeneration.current) inFlight.current = false;
    }
  };

  useEffect(() => {
    if (user.preview) return undefined;
    load();
    if (hasConnection === false) return undefined;
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, hasConnection, user.preview, activeChannel]);

  const switchChannel = (key) => {
    if (key === activeChannel) return;
    loadGeneration.current += 1;
    inFlight.current = false;
    setCanReviewEdits(false);
    setActiveChannel(key);
    setActivity(user.preview ? demoActivity : { applies: [], runs: [] });
    setPerformance(user.preview ? demoPerformance : { campaigns: [] });
    setHasConnection(user.preview ? true : null);
    setError(null);
    performanceLoadedAt.current = 0;
  };

  const undo = async (apply) => {
    setUndoing(apply.id);
    try {
      await undoApply({ userId, connectionId: apply.connection_id, applyId: apply.id });
      await load();
    } catch (undoError) {
      setError(campaignErrorMessage(undoError, 'Failed to undo change'));
    } finally {
      setUndoing(null);
    }
  };

  const applied = activity.applies.filter((item) => item.status === 'applied').length;
  const failed = activity.applies.filter((item) => item.status === 'failed').length;
  const pendingUndos = new Set(activity.applies.filter((item) => item.undo_of && item.status === 'pending').map((item) => item.undo_of));
  const totals = performance.campaigns.reduce((sum, campaign) => ({
    spend: sum.spend + campaign.current.spend,
    clicks: sum.clicks + campaign.current.clicks,
    impressions: sum.impressions + campaign.current.impressions,
    results: sum.results + campaign.current.results,
  }), { spend: 0, clicks: 0, impressions: 0, results: 0 });
  const currency = performance.campaigns[0]?.current.currency || (channel.key === 'universal-ads' ? null : 'USD');
  const money = new Intl.NumberFormat('en-US', currency ? { style: 'currency', currency } : { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Activity</p>
          <h1 className="page-title">Every move, accounted for.</h1>
          <p className="page-subtitle">{channel.copy.activitySubtitle}</p>
        </div>
      </header>

      <ChannelTabs active={activeChannel} onSelect={switchChannel} />

      {error && <div className="notice notice-error" role="alert">{error}</div>}

      {performance.campaigns.length > 0 && (
        <>
          <div className="agent-turn">
            <span className="agent-avatar">A</span>
            <div>
              <strong>Your campaigns generated {totals.results || totals.clicks} {activeChannel === 'universal-ads' ? 'video completions' : totals.results ? 'results' : 'clicks'} in the last 7 days.</strong>
              <p>{money.format(totals.spend)} spent across {performance.campaigns.length} watched campaign{performance.campaigns.length === 1 ? '' : 's'}. Performance below compares with the previous 7 days.</p>
            </div>
          </div>
          <section className="card performance-card">
            <div className="performance-grid performance-header"><span>Campaign</span><span>Spend</span><span>Impressions</span><span>Clicks</span><span>CTR</span><span>{activeChannel === 'universal-ads' ? 'Video completions' : 'Results'}</span></div>
            {performance.campaigns.map((campaign) => (
              <div key={campaign.platform_campaign_id} className="performance-grid performance-row">
                <strong>{campaign.name || campaign.platform_campaign_id}</strong>
                <span>{campaign.current.spend == null ? '—' : money.format(campaign.current.spend)} <small className={campaign.change.spend_pct > 0 ? 'trend-up' : 'trend-down'}>{campaign.change.spend_pct == null ? '—' : `${campaign.change.spend_pct > 0 ? '+' : ''}${campaign.change.spend_pct.toFixed(0)}%`}</small></span>
                <span>{campaign.current.impressions?.toLocaleString() ?? '—'}</span>
                <span>{campaign.current.clicks?.toLocaleString() ?? '—'} <small className={campaign.change.clicks_pct >= 0 ? 'trend-up' : 'trend-down'}>{campaign.change.clicks_pct == null ? '—' : `${campaign.change.clicks_pct > 0 ? '+' : ''}${campaign.change.clicks_pct.toFixed(0)}%`}</small></span>
                <span>{campaign.current.ctr == null ? '—' : `${campaign.current.ctr.toFixed(2)}%`} <small className={campaign.change.ctr_points >= 0 ? 'trend-up' : 'trend-down'}>{campaign.change.ctr_points == null ? '—' : `${campaign.change.ctr_points > 0 ? '+' : ''}${campaign.change.ctr_points.toFixed(2)} pts`}</small></span>
                <span>{campaign.current.results ?? '—'} <small className={campaign.change.results_pct >= 0 ? 'trend-up' : 'trend-down'}>{campaign.change.results_pct == null ? '—' : `${campaign.change.results_pct > 0 ? '+' : ''}${campaign.change.results_pct.toFixed(0)}%`}</small></span>
              </div>
            ))}
          </section>
        </>
      )}

      <div className="metric-strip">
        <div className="metric"><strong>{applied}</strong><span>Changes applied</span></div>
        <div className="metric"><strong>{activity.runs.length}</strong><span>Agent runs</span></div>
        <div className="metric"><strong>{failed || '—'}</strong><span>{failed ? 'Need attention' : 'No failed changes'}</span></div>
      </div>

      <div className="activity-grid">
        <section className="card">
          <div className="section-heading" style={{ marginTop: 0 }}><h2>Approved changes</h2><span>{activity.applies.length} total</span></div>
          {activity.applies.length === 0 && <p className="card-copy">Your approved campaign changes will appear here.</p>}
          <div className="activity-list">
            {activity.applies.map((apply) => {
              const change = changeValues(apply);
              const occurredAt = apply.status === 'reverted' ? apply.reverted_at : apply.applied_at;
              return (
                <div key={apply.id} className="activity-row">
                  <span className={`status-dot status-${apply.status}`} />
                  <div>
                    <strong>{apply.campaign_name || apply.recommendation_title || 'Campaign change'}</strong>
                    <code className="activity-tool">{applyToolFor(channel, apply.action_type)}</code>
                    {(change || occurredAt) && <p>{change}{change && occurredAt && ' · '}{occurredAt && new Date(occurredAt).toLocaleString()}</p>}
                    {apply.processing_id && <p className="run-receipt">Run {apply.processing_id}</p>}
                    {['facebook', 'chatgpt-ads'].includes(channel.key) && apply.status === 'applied'
                      && !pendingUndos.has(apply.id) && apply.before_state?.restore_payload && (
                      <button type="button" className="text-button" disabled={undoing === apply.id} onClick={() => undo(apply)}>
                        {undoing === apply.id ? 'Undoing…' : 'Undo change'}
                      </button>
                    )}
                    {apply.error && <p className="activity-error">What happened: {friendlyCampaignError(apply.error)}</p>}
                    {apply.after_state?.provider_error && (
                      <p className="activity-error">{friendlyCampaignError(apply.after_state.provider_error)}</p>
                    )}
                  </div>
                  <span className="activity-status">{applyStatus(apply)}</span>
                </div>
              );
            })}
          </div>
        </section>

        {(activity.edits || []).length > 0 && <section className="card">
          <h2>Reviewed campaign settings</h2>
          {activity.edits.map((edit) => <div className="meta-edit-activity-row" key={edit.id}>
            <div><MetaEditReview summary={edit.summary} />
              <p>{new Date(edit.created_at).toLocaleString()} · {edit.status}</p>
              {edit.outcomes?.map((row) => <p key={row.id}>{row.name}: {row.status.replaceAll('_', ' ')}
                {row.observed_labels && <> · Observed: {row.observed_labels.map(({ field, value }) => `${field}: ${value}`).join('; ')}</>}</p>)}
              {canReviewEdits && ['applying', 'partial', 'unconfirmed'].includes(edit.status) && <button type="button" className="btn" disabled={checkingEdit != null}
                onClick={async () => {
                  setCheckingEdit(edit.id);
                  try { await (edit.summary.platform === 'Google Ads' ? decideGoogleBudgetEdit : decideMetaEdit)({ connectionId: edit.connection_id, editId: edit.id, action: 'reconcile' }); await load(); }
                  catch (err) { setError(campaignErrorMessage(err)); } finally { setCheckingEdit(null); }
                }}>{checkingEdit === edit.id ? 'Checking…' : 'Check current result'}</button>}
              {canReviewEdits && ['partial', 'unconfirmed'].includes(edit.status) && edit.outcomes?.every((row) => !row.attempted_at || row.reconciled_at) && <div>
                <p>Closing this review preserves uncertain results and sends no further changes. A new edit requires fresh review and approval.</p>
                <button type="button" className="btn" disabled={checkingEdit != null} onClick={async () => {
                  setCheckingEdit(edit.id);
                  try { await (edit.summary.platform === 'Google Ads' ? decideGoogleBudgetEdit : decideMetaEdit)({ connectionId: edit.connection_id, editId: edit.id, action: 'close' }); await load(); }
                  catch (err) { setError(campaignErrorMessage(err)); } finally { setCheckingEdit(null); }
                }}>Acknowledge result and close review</button>
              </div>}
            </div>
          </div>)}
        </section>}
        <section className="card">
          <div className="section-heading" style={{ marginTop: 0 }}><h2>Agent runs</h2><span>{activity.runs.length} total</span></div>
          {activity.runs.length === 0 && <p className="card-copy">No agent runs yet.</p>}
          <div className="activity-list">
            {activity.runs.map((run) => (
              <div key={run.id} className="activity-row">
                <span className={`status-dot status-${run.status}`} />
                <div>
                  <strong>{run.campaign_name || 'Campaign analysis'}</strong>
                  <code className="activity-tool">{channel.tools.read} → {channel.tools.plan}</code>
                  {runWindow(run) && <p className="run-window">{runWindow(run)}</p>}
                  <p>{run.started_at ? new Date(run.started_at).toLocaleString() : 'Queued'}</p>
                  {run.processing_id && <p className="run-receipt">Run {run.processing_id}</p>}
                  {run.error && (
                    <p className={run.status === 'skipped' ? 'activity-advice' : 'activity-error'}>
                      {run.status === 'skipped' ? noDeliveryAdvice : friendlyCampaignError(run.error)}
                    </p>
                  )}
                  {run.status === 'completed' && !run.error && Number(run.open_recommendation_count) > 0 && (
                    <p className="activity-outcome">
                      {run.open_recommendation_count} recommendation{Number(run.open_recommendation_count) === 1 ? '' : 's'} ready for review.{' '}
                      <Link to={user.preview ? '/demo/recommendations' : '/recommendations'}>Review →</Link>
                    </p>
                  )}
                  {run.status === 'completed' && !run.error && Number(run.open_recommendation_count) === 0 && (
                    <p className="activity-outcome">
                      {Number(run.recommendation_count) > 0
                        ? 'Recommendations reviewed. No action needed.'
                        : 'No safe change recommended. No action needed.'}
                    </p>
                  )}
                </div>
                <span className="activity-status">{run.status}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default ActivityPage;
